import { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';

const NotificationContext = createContext(null);

const playNotificationSound = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    // Clean high-quality notification chime
    osc.frequency.setValueAtTime(880, ctx.currentTime); // A5
    osc.frequency.setValueAtTime(1320, ctx.currentTime + 0.08); // E6
    
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.12, ctx.currentTime + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
  } catch (err) {
    console.warn('[Notification Sound] AudioContext block or failure:', err);
  }
};

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const toast = useToast();
  const toastRef = useRef(toast);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const [permission, setPermission] = useState(Notification.permission);

  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const MAX_RETRY_DELAY = 30000;
  const INITIAL_RETRY_DELAY = 1000;
  const MAX_RETRIES = 10;

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) return;
    const perm = await Notification.requestPermission();
    setPermission(perm);
  }, []);

  const showSystemNotification = useCallback((title, options) => {
    if (permission === 'granted' && 'Notification' in window) {
      new Notification(title, options);
    }
  }, [permission]);

  const handleNotification = useCallback((msg) => {
    switch (msg.type) {
      case 'clip.create':
        toastRef.current.info(msg.data.content.slice(0, 100) + (msg.data.content.length > 100 ? '...' : ''), '📋 New Clipboard Entry');
        showSystemNotification('📋 New Clipboard Entry', {
          body: msg.data.content.slice(0, 100),
          icon: 'https://x9ogt2f8d1hnsynj.public.blob.vercel-storage.com/Syncvault/icon.png'
        });
        window.dispatchEvent(new CustomEvent('syncvault:refresh:clips', { detail: msg.data }));
        break;
      case 'snap.create':
        toastRef.current.success(msg.data.filename, '📷 New Snapshot Uploaded');
        showSystemNotification('📷 New Snapshot Uploaded', {
          body: msg.data.filename,
          icon: 'https://x9ogt2f8d1hnsynj.public.blob.vercel-storage.com/Syncvault/icon.png'
        });
        window.dispatchEvent(new CustomEvent('syncvault:refresh:snaps', { detail: msg.data }));
        break;
      case 'shared_clip.receive':
        playNotificationSound();
        toastRef.current.success(`From @${msg.data.sender_username}: ${msg.data.content.slice(0, 50)}${msg.data.content.length > 50 ? '...' : ''}`, '✉️ New Shared Clip');
        showSystemNotification('✉️ New Shared Clip', {
          body: `From @${msg.data.sender_username}: ${msg.data.content.slice(0, 100)}`,
          icon: 'https://x9ogt2f8d1hnsynj.public.blob.vercel-storage.com/Syncvault/icon.png'
        });
        window.dispatchEvent(new CustomEvent('syncvault:refresh:shared', { detail: msg.data }));
        break;
      default:
        console.log('[notifications] Unknown message type', msg.type);
    }
  }, [showSystemNotification]);

  const connect = useCallback(() => {
    if (!isAuthenticated) {
      console.log('[notifications] Not connecting: not authenticated');
      return;
    }

    if (retryCountRef.current >= MAX_RETRIES) {
      console.warn('[notifications] Max reconnection retries reached. Stopping.');
      toastRef.current.error('Real-time connection failed after multiple attempts. Please refresh.', 'Connection Lost');
      return;
    }

    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    console.log(`[notifications] Attempting connection (Attempt ${retryCountRef.current + 1})...`);
    const apiBase = import.meta.env.VITE_API_BASE || '/api';
    
    let wsUrl;
    if (apiBase.startsWith('http')) {
      wsUrl = new URL(apiBase.replace(/^http/, 'ws'));
    } else {
      wsUrl = new URL(apiBase, window.location.origin.replace(/^http/, 'ws'));
    }
    
    wsUrl.pathname = wsUrl.pathname.replace(/\/$/, '') + '/notifications/stream';

    // The browser will automatically include HttpOnly cookies in this WebSocket upgrade request
    const ws = new WebSocket(wsUrl.href);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[notifications] WS Connected');
      retryCountRef.current = 0;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        handleNotification(message);
      } catch (err) {
        console.error('[notifications] WS Parse error:', err);
      }
    };

    ws.onclose = (e) => {
      console.log('[notifications] WS Closed:', e.code);
      wsRef.current = null;
      
      if (isAuthenticated && e.code !== 1000) {
        const delay = Math.min(INITIAL_RETRY_DELAY * Math.pow(2, retryCountRef.current), MAX_RETRY_DELAY);
        console.log(`[notifications] Will reconnect in ${delay}ms...`);
        
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = setTimeout(() => {
          retryCountRef.current++;
          connect();
        }, delay);
      }
    };

    ws.onerror = (err) => {
      console.error('[notifications] WS Error:', err);
    };
  }, [isAuthenticated, handleNotification]);

  useEffect(() => {
    console.log('[notifications] Effect running. isAuthenticated:', isAuthenticated);
    if (isAuthenticated) {
      connect();
    } else {
      if (wsRef.current) {
        console.log('[notifications] Closing socket due to unauthentication');
        wsRef.current.close();
      }
    }
    return () => {
      if (wsRef.current) {
        console.log('[notifications] Effect cleanup: closing socket');
        wsRef.current.close();
      }
    };
  }, [isAuthenticated, connect]);

  return (
    <NotificationContext.Provider value={{ permission, requestPermission }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};
